import { useEffect } from 'react';
import { onSessionEnded } from '../auth/expiry.ts';
import { refreshKept } from './keep.ts';
import { drainQueue, refreshQueueCounts } from './queue.ts';
import { resetOfflineState, setOfflineState } from './state.ts';
import { dropCopy, openCopyFor, readMeta } from './store.ts';

// When the offline copy is opened, refreshed, and destroyed.
//
// Opened: as soon as an account is signed in and known, under that
// account's id. Refreshed: on that first open and every time the browser
// says the connection is back, which is also when the queue drains.
// Destroyed: on sign-out, and on the 401 that says the session is gone —
// which is what a device sees after "sign the account out everywhere", so a
// lost phone clears its copy the moment it next reaches the Worker.
//
// Every one of these is best-effort: a browser with no IndexedDB, or one
// that refused the database, must still run the app, and a refresh that
// cannot reach the server must not break the page that triggered it.

/** Open the copy for an account and bring it up to date. */
export async function startOfflineCopy(userId: string): Promise<void> {
  await openCopyFor(userId);
  const meta = await readMeta();
  // The banner shows the queue and the last sync from the moment the app
  // opens, before any refresh has had time to run.
  setOfflineState({ syncedAt: meta.syncedAt });
  await refreshQueueCounts();
  await catchUp();
}

/** Send what is queued and refresh what is kept; safe to call when offline. */
export async function catchUp(): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return;
  }
  await drainQueue();
  await refreshKept();
}

/** Close and delete the copy, and forget what the app was saying about it. */
export async function stopOfflineCopy(): Promise<void> {
  await dropCopy();
  resetOfflineState();
}

/** Report a failure that must not take the page down with it. */
function report(what: string, error: unknown): void {
  console.warn(what, error);
}

/**
 * Keep the copy in step with the session and the connection for as long as
 * the app is mounted: open it for the signed-in account, delete it when the
 * session ends, and catch up whenever the browser comes back online.
 */
export function useOfflineCopy(userId: string | null): void {
  useEffect(() => {
    if (userId === null) {
      void stopOfflineCopy().catch((error: unknown) => {
        report('The offline copy could not be cleared', error);
      });
      return;
    }
    void startOfflineCopy(userId).catch((error: unknown) => {
      report('The offline copy could not be opened', error);
    });
    const onOnline = () => {
      void catchUp().catch((error: unknown) => {
        report('The offline changes could not be sent', error);
      });
    };
    window.addEventListener('online', onOnline);
    const stopWatchingSession = onSessionEnded(() => {
      void stopOfflineCopy().catch((error: unknown) => {
        report('The offline copy could not be cleared', error);
      });
    });
    return () => {
      window.removeEventListener('online', onOnline);
      stopWatchingSession();
    };
  }, [userId]);
}
